import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditSample,
  getIsSampleModalOpen,
  toggleSampleModal,
} from "../../features/samples/samplesSlice";
import Select from "react-select";
import { selectStyles } from "../../static/styles";

import styles from "./SampleFormModal.module.scss";
import { ISampleFormInput, OptionsProps } from "../../types/sample";

type Props = {
  handleSaveSample: (data: ISampleFormInput) => void;
};

const SampleFormModal = ({ handleSaveSample }: Props) => {
  const dispatch = useAppDispatch();
  const editSample = useAppSelector(getEditSample);
  const isSampleModalOpen = useAppSelector(getIsSampleModalOpen);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [partyOption, setPartyOption] = useState<OptionsProps[]>([]);
  const [unitOption] = useState<OptionsProps[]>([
    {
      value: 1,
      label: "વજન - KG",
    },
    {
      value: 2,
      label: "નંગ - Piece",
    },
    {
      value: 3,
      label: "જોડી - Pair",
    },
  ]);
  const [statusOption] = useState<OptionsProps[]>([
    {
      value: 0,
      label: "ચાલુ કામ",
    },
    {
      value: 1,
      label: "પૂરું થયેલું કામ",
    },
  ]);

  const {
    handleSubmit,
    register,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<ISampleFormInput>({
    defaultValues: {
      qty: 0,
      delivered_qty: 0,
      unit: 0,
      amount: 0,
      status: 0,
    },
  });

  const handleOnSubmit: SubmitHandler<ISampleFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveSample(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleSampleModal(false));
  };

  useEffect(() => {
    if (editSample && editSample.id) {
      reset({
        qty: editSample.qty,
        delivered_qty: editSample.delivered_qty,
        unit: editSample.unit,
        amount: editSample.amount,
        status: editSample.status,
      });
    } else {
      reset({
        qty: 0,
        delivered_qty: 0,
        unit: 0,
        amount: 0,
        status: 0,
      });
    }
  }, [editSample, isSampleModalOpen]);

  return (
    <>
      {isSampleModalOpen === true && (
        <div className={styles.SampleFormModal}>
          <div className="relative h-[100vh] w-[100vw] overflow-y-scroll lg:overflow-auto top-0 lg:top-10 lg:mx-auto p-5 border lg:w-96 lg:h-fit shadow-lg lg:rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-2xl text-center font-bold leading-6 text-primary-1">
                સેમ્પલ ઓર્ડર ની માહિતી
              </h3>
              <div className="mt-2 py-3">
                <form onSubmit={handleSubmit(handleOnSubmit)}>
                  <div className="grid gap-4">
                    <div className="mt-4">
                      <label className="text-primary-1 font-bold">
                        પાર્ટી :
                      </label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="text"
                          placeholder="પાર્ટી નું નામ સિલેક્ટ કરો"
                          readOnly
                          value={editSample?.party_name || "-"}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-primary-1 font-bold">આઈટમ :</label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="text"
                          placeholder="આઈટમ નું નામ સિલેક્ટ કરો"
                          readOnly
                          value={editSample?.item_name || "-"}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-primary-1 font-bold">
                        પ્રોસેસ :
                      </label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="text"
                          placeholder="પ્રોસેસ નું નામ સિલેક્ટ કરો"
                          readOnly
                          value={editSample?.process_name || "-"}
                        />
                      </div>
                    </div>

                    <div className="mt-1">
                      <label className="text-primary-1 font-bold">Qty</label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="number"
                          step={"any"}
                          placeholder={`Qty દાખલ કરો`}
                          {...register("qty", { required: true })}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                        />
                      </div>
                      {errors.qty && (
                        <span className="text-red-500">Qty દાખલ કરો</span>
                      )}
                    </div>

                    <div className="mt-1">
                      <label className="text-primary-1 font-bold">
                        Delivered Qty
                      </label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="number"
                          step={"any"}
                          placeholder={`Delivered Qty દાખલ કરો`}
                          {...register("delivered_qty")}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                        />
                      </div>
                      {errors.delivered_qty && (
                        <span className="text-red-500">
                          Delivered Qty દાખલ કરો
                        </span>
                      )}
                    </div>

                    <div className="mt-1">
                      <label className="text-primary-1 font-bold">
                        યુનિટ :
                      </label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                        <Controller
                          {...register("unit", {
                            required: true,
                            min: 1,
                          })}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              value={
                                unitOption.find(
                                  (val) => val.value === field?.value
                                ) || null
                              }
                              className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                              styles={selectStyles}
                              placeholder={"યુનિટ નું નામ દાખલ કરો"}
                              options={unitOption}
                              onChange={(val: any) =>
                                setValue("unit", val?.value)
                              }
                            />
                          )}
                        />
                      </div>

                      {errors?.unit && (
                        <span className="text-red-500">
                          યુનિટ નું નામ દાખલ કરો
                        </span>
                      )}
                    </div>

                    <div className="mt-1">
                      <label className="text-primary-1 font-bold">
                        અમાઉન્ટ
                      </label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-14 w-full relative rounded">
                        <input
                          className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          type="number"
                          step={"any"}
                          placeholder={`અમાઉન્ટ દાખલ કરો`}
                          {...register("amount")}
                          onFocus={(e) =>
                            e.target.addEventListener(
                              "wheel",
                              function (e) {
                                e.preventDefault();
                              },
                              { passive: false }
                            )
                          }
                        />
                      </div>
                      {errors.amount && (
                        <span className="text-red-500">અમાઉન્ટ દાખલ કરો</span>
                      )}
                    </div>

                    <div>
                      <label className="text-primary-1 font-bold">
                        સ્ટેટ્સ :
                      </label>
                      <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 lg:h-12 w-full relative rounded">
                        <Controller
                          defaultValue={0}
                          {...register("status", { required: true })}
                          control={control}
                          render={({ field }) => (
                            <Select
                              {...field}
                              value={
                                statusOption.find(
                                  (val) => val.value === field.value
                                ) || null
                              }
                              className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                              styles={selectStyles}
                              placeholder={"સ્ટેટ્સ નું નામ સિલેક્ટ કરો"}
                              options={statusOption}
                              onChange={(val: any) =>
                                setValue("status", val?.value)
                              }
                            />
                          )}
                        />
                      </div>
                      {errors.status && errors.status.type === "required" && (
                        <span className="text-red-500">
                          સ્ટેટ્સ નું નામ સિલેક્ટ કરો
                        </span>
                      )}
                    </div>

                    <div className="mt-3 text-center">
                      <button
                        className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? "રાહ જુઓ..." : "આગળ વધો"}
                      </button>
                    </div>

                    <div className="mt-1 text-center">
                      <button
                        className="mx-auto shadow disabled:opacity-50 rounded-3xl text-3xl lg:text-xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                        type="button"
                        onClick={handleCloseFormModal}
                        disabled={isLoading}
                      >
                        બંધ કરો
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SampleFormModal;
